import { AnnatureForm } from '@buyerflo/core/schemas/annature'
import { DocusignForm } from '@buyerflo/core/schemas/docusign'
import {
    EsignForm,
    EsignFormData,
    EsignFormType,
    getEsignFormFromPurchaser,
    getFormName,
    SigningService,
} from '@buyerflo/core/schemas/esign'
import { Clock, Receipt } from '@tamagui/lucide-icons-2'
import { useMutation } from '@tanstack/react-query'
import { useUsersApi } from 'apiProviders'
import { useUserData } from 'auth'
import Button from 'components/button'
import {
    AddRounded,
    Cancel,
    Check,
    DeleteRounded,
    ErrorOutlineRounded,
    MoreVertRounded,
    MoveToInbox,
    ScheduleSendRounded,
    UploadFileRounded,
    Visibility,
} from 'components/icons'
import Menu from 'components/menu'
import PdfModal from 'components/pdfModal'
import { useShowSnack } from 'components/snackbar'
import OptionalTooltip from 'links/optionalTooltip'
import { useEffect, useState } from 'react'
import { SizableText, Spinner, Text, View, XStack, YStack } from 'tamagui'
import { useTRPC, useTRPCClient } from 'utils/trpc'
import { usePurchaserContext } from '../purchaserProvider'
import AbandonFormModal from '../sharedModals/abandonFormModal'
import AddAttachmentModal from '../sharedModals/addAttachmentsModal'
import IframeModal from '../sharedModals/iframeModal'
import UploadFormModal from '../sharedModals/uploadFormModal'
import ManualFormUploadModal from './manualUploadFormModal'
import PdfFormEditorModal from './pdfFormEditorModal'

enum FormStage {
    'voided' = -2,
    'declined' = -1,
    'none' = 0,
    'createdInRei',
    'created',
    'sent',
    'completed',
    'manual',
}

const getFormStage = (esignForm: EsignForm | undefined): FormStage => {
    if (esignForm) {
        switch (esignForm.formStatus) {
            case 'draft':
            case 'created':
                return FormStage.created
            case 'sent':
            case 'delivered':
                return FormStage.sent
            case 'completed':
                return FormStage.completed
            case 'manual upload':
                return FormStage.manual
            case 'declined':
                return FormStage.declined
            case 'voided':
                return FormStage.voided
            default:
                return FormStage.none
        }
    }

    return FormStage.none
}

interface NormalizedRecipient {
    id: string
    name: string
    status: string
    declined_reason?: string
}

/** Normalize recipients from either Annature or DocuSign into a common shape */
const getRecipientsList = (form: EsignFormData): NormalizedRecipient[] => {
    if ('envelopeId' in form) {
        // DocuSign
        return (
            (form as DocusignForm).recipients?.signers?.map(s => ({
                id: s.recipientId,
                name: s.name,
                status: s.status,
            })) ?? []
        )
    }
    if ('id' in form && 'recipients' in form && Array.isArray((form as AnnatureForm).recipients)) {
        // Annature
        return (form as AnnatureForm).recipients.map(r => ({
            id: r.id,
            name: r.name,
            status: r.status,
            declined_reason: r.declined_reason,
        }))
    }
    return []
}

interface Props {
    title: string
    formType: EsignFormType
    createDisabled: boolean | string
    validate: () => Promise<boolean> | boolean
    preCreate: () => Promise<boolean> | boolean
    roleNames: string[]
    signingService?: SigningService
}

const formatCountdown = (scheduledSendAt: number) => {
    const diff = scheduledSendAt - Date.now()
    if (diff <= 0) return 'Sending now...'
    const hours = Math.floor(diff / (1000 * 60 * 60))
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
    if (hours > 0) return `Invoice will be sent in ${hours}h ${minutes}m`
    return `Invoice will be sent in ${minutes}m`
}

// Helper functions for invoice step
const getInvoiceStepColor = (
    invoice: { xeroStatus?: string; scheduledSendAt?: number; sendError?: string } | undefined,
    formCompleted: boolean,
): string | undefined => {
    if (!invoice && formCompleted) return 'red'
    if (invoice?.sendError) return 'red'
    if (invoice?.scheduledSendAt) return 'yellow'
    return undefined
}

const getInvoiceStepIcon = (
    invoice: { xeroStatus?: string; scheduledSendAt?: number; sendError?: string } | undefined,
    formCompleted: boolean,
) => {
    if (!invoice && formCompleted) {
        return (
            <ErrorOutlineRounded
                size={14}
                color="white"
            />
        )
    }
    if (!invoice) return undefined

    // Error state
    if (invoice.sendError) {
        return (
            <ErrorOutlineRounded
                size={14}
                color="white"
            />
        )
    }

    // Scheduled state
    if (invoice.scheduledSendAt) {
        return (
            <ScheduleSendRounded
                size={14}
                color="white"
            />
        )
    }

    // Check xeroStatus for sent/created states
    if (invoice.xeroStatus === 'AUTHORISED' || invoice.xeroStatus === 'PAID') {
        return (
            <Check
                size={14}
                color="white"
            />
        )
    }

    // Draft or created state
    if (invoice.xeroStatus === 'DRAFT') {
        return (
            <Receipt
                size={14}
                color="$gray11"
            />
        )
    }

    return undefined
}

const getInvoiceStepSubtext = (
    invoice: { xeroStatus?: string; scheduledSendAt?: number; sendError?: string } | undefined,
    formCompleted: boolean,
): string => {
    if (!invoice) return formCompleted ? 'Not created' : ''

    if (invoice.sendError) return 'Failed to send'
    if (invoice.scheduledSendAt) {
        return formatCountdown(invoice.scheduledSendAt).replace('Invoice will be sent in', 'Sending in')
    }

    switch (invoice.xeroStatus) {
        case 'DRAFT':
            return 'Created in Xero'
        case 'AUTHORISED':
            return 'Sent'
        case 'PAID':
            return 'Sent and paid'
        default:
            return ''
    }
}

// Helper functions for invoice payment step
const getInvoicePaymentStepColor = (xeroStatus?: string): string | undefined => {
    if (xeroStatus === 'PAID') return undefined // Completed, use default blue
    return undefined // Pending, use default gray
}

const getInvoicePaymentStepIcon = (xeroStatus?: string) => {
    if (xeroStatus === 'PAID') {
        return (
            <Check
                size={14}
                color="white"
            />
        )
    }
    if (!xeroStatus) return undefined
    // Awaiting payment - show clock icon
    return (
        <Clock
            size={14}
            color="white"
        />
    )
}

const getInvoicePaymentStepSubtext = (xeroStatus?: string): string => {
    if (xeroStatus === 'PAID') return 'Paid'
    if (!xeroStatus) return ''
    return 'Awaiting payment'
}

const StepCircle = ({
    active,
    completed,
    color,
    children,
}: {
    active?: boolean
    completed?: boolean
    color?: string
    children?: React.ReactNode
}) => {
    let bg = '$gray5'

    if (completed) {
        bg = color === 'danger' || color === 'red' ? '$red9' : '$green9'
    } else if (color) {
        // Non-completed steps with explicit colors (warning/error states)
        if (color === 'red') {
            bg = '$red9'
        } else if (color === 'yellow') {
            bg = '$yellow9'
        }
    }

    return (
        <View
            width={28}
            height={28}
            borderRadius={14}
            backgroundColor={bg}
            alignItems="center"
            justifyContent="center"
        >
            {children}
        </View>
    )
}

const StepLine = ({ active }: { active?: boolean }) => (
    <View
        width={2}
        flex={1}
        backgroundColor={active ? '$green9' : '$gray5'}
        alignSelf="center"
        minHeight={20}
    />
)

const StepItem = ({
    indicator,
    active,
    isLast,
    children,
}: {
    indicator: React.ReactNode
    active?: boolean
    isLast?: boolean
    children: React.ReactNode
}) => (
    <XStack gap="$3">
        <YStack alignItems="center">
            {indicator}
            {!isLast && <StepLine active={active} />}
        </YStack>
        <YStack
            flex={1}
            paddingBottom={isLast ? 0 : '$3'}
        >
            {/* Center the label against the 28px step circle so single-line steps line up with the check */}
            <YStack
                minHeight={28}
                justifyContent="center"
            >
                {children}
            </YStack>
        </YStack>
    </XStack>
)

const EsignFormStepper = ({
    title,
    formType,
    createDisabled,
    validate,
    preCreate,
    roleNames,
    signingService = 'annature',
}: Props) => {
    const { purchaser, refreshPurchaser, setPurchaser } = usePurchaserContext()
    const showSnack = useShowSnack()
    const usersApi = useUsersApi()
    const { user } = useUserData()
    const trpc = useTRPC()
    const trpcClient = useTRPCClient()
    const createFormMutation = useMutation(trpc.esign.createForm.mutationOptions())

    const [previewPdf, setPreviewPdf] = useState<{ url: string; useAuth?: boolean; isRawPdf?: boolean }>()
    const [editUrl, setEditUrl] = useState<string>()
    const [showUploadForm, setShowUploadForm] = useState(false)
    const [showAbandonFormDialog, setShowAbandonFormDialog] = useState<EsignFormType | undefined>()
    const [showAddAttachmentsModal, setShowAddAttachmentsModal] = useState(false)
    const [showManualUploadModal, setShowManualUploadModal] = useState<EsignFormType | undefined>()
    const [showPdfEditor, setShowPdfEditor] = useState(false)
    const [creatingForm, setCreatingForm] = useState(false)
    const [, setTick] = useState(0)

    // Tick every minute to update countdown for scheduled invoices
    useEffect(() => {
        if (!purchaser.engagementInvoice?.scheduledSendAt) return
        const interval = setInterval(() => setTick(t => t + 1), 60_000)
        return () => clearInterval(interval)
    }, [purchaser.engagementInvoice?.scheduledSendAt])

    const form = getEsignFormFromPurchaser({ purchaser, formType })

    const formStage = getFormStage(form as EsignForm)

    const formName = getFormName(formType)

    const agent = usersApi.data?.find(user => user.userId == purchaser.agents[0].userId)

    const manualFileUpload = false

    const createDisabledBoolean = typeof createDisabled == 'string' ? true : createDisabled

    const recipients = form ? getRecipientsList(form) : []

    const CreateEsignFormButton = () => (
        <Button
            theme="primary"
            data-tour="create-form-button"
            disabled={createDisabledBoolean || manualFileUpload || creatingForm}
            icon={creatingForm ? <Spinner /> : <AddRounded />}
            onPress={async () => {
                if (creatingForm) return
                setCreatingForm(true)
                try {
                    if (await validate()) {
                        const pass = await preCreate()
                        if (pass) {
                            if (manualFileUpload) {
                                setShowUploadForm(true)
                            } else if (signingService === 'docusign' && formType === 'agentAppointmentForm') {
                                // DocuSign: edit the PDF fields in-app, then create + send
                                setShowPdfEditor(true)
                            } else {
                                const createData = await createFormMutation.mutateAsync({
                                    purchaserId: purchaser.purchaserId,
                                    formType: formType,
                                    s3Keys: [],
                                })
                                if (createData) {
                                    const { updatedPurchaser, editUrl } = createData
                                    setPurchaser(updatedPurchaser)
                                    setEditUrl(editUrl)
                                } else {
                                    await refreshPurchaser()
                                }
                                showSnack(`${formName} created`, 'success')
                            }
                        }
                    }
                } catch (error: any) {
                    console.error('Error creating form:', error)
                    showSnack(error.message || 'Error creating form', 'danger')
                } finally {
                    setCreatingForm(false)
                }
            }}
        >
            {manualFileUpload ? 'Upload' : 'Create'} Form
        </Button>
    )

    const ViewFormButton = () => (
        <Button
            variant="outlined"
            onPress={async () => {
                switch (formStage) {
                    case FormStage.completed:
                    case FormStage.manual:
                        try {
                            const url = await trpcClient.esign.viewCompletedForm.query({
                                purchaserId: purchaser.purchaserId,
                                formType: formType as 'agentAppointmentForm',
                            })
                            setPreviewPdf({
                                url,
                                useAuth: false,
                            })
                        } catch (e) {
                            console.error(e)
                            showSnack('Error loading completed form', 'danger')
                        }
                        break
                    case FormStage.sent:
                        try {
                            const sentUrl = await trpcClient.esign.viewSentForm.query({
                                purchaserId: purchaser.purchaserId,
                                formType: formType as 'agentAppointmentForm',
                            })
                            setPreviewPdf({
                                url: sentUrl,
                                useAuth: false,
                                isRawPdf: true,
                            })
                        } catch (e) {
                            console.error(e)
                            showSnack('Error loading sent form', 'danger')
                        }
                        break
                    case FormStage.created:
                        try {
                            const viewData = await trpcClient.esign.getEditUrl.query({
                                purchaserId: purchaser.purchaserId,
                                formType: formType as 'agentAppointmentForm',
                            })
                            setEditUrl(viewData.url)
                        } catch (e) {
                            console.error(e)
                            showSnack('Error loading edit URL', 'danger')
                        }
                        break
                    case FormStage.createdInRei:
                        try {
                            const data = await trpcClient.esign.getReiFormUrl.query({
                                purchaserId: purchaser.purchaserId,
                                formType: formType,
                            })
                            window.open(data.url, '_blank')
                        } catch (e) {
                            console.error(e)
                            showSnack('Error loading REI form', 'danger')
                        }
                        break
                    default:
                        try {
                            const defaultUrl = await trpcClient.esign.viewSentForm.query({
                                purchaserId: purchaser.purchaserId,
                                formType: formType as 'agentAppointmentForm',
                            })
                            setPreviewPdf({
                                url: defaultUrl,
                                useAuth: false,
                                isRawPdf: true,
                            })
                        } catch (e) {
                            console.error(e)
                            showSnack('Error loading form', 'danger')
                        }
                        break
                }
            }}
            icon={<Visibility />}
        >
            View Form
        </Button>
    )

    // HeaderSection Component
    const HeaderSection = () => (
        <XStack
            gap="$4"
            alignItems="center"
            width="100%"
        >
            <SizableText
                size="$5"
                fontWeight="600"
            >
                {title}
            </SizableText>
            <Menu>
                <Menu.Trigger asChild>
                    <Button
                        size="$2"
                        variant="outlined"
                        width="$2"
                        paddingHorizontal={0}
                    >
                        <MoreVertRounded size={16} />
                    </Button>
                </Menu.Trigger>
                <Menu.Portal>
                    <Menu.Content>
                        {formStage < FormStage.completed && (
                            <Menu.Item
                                key="upload"
                                onPress={async () => {
                                    const pass = await preCreate()
                                    if (pass) {
                                        setShowManualUploadModal(formType)
                                    }
                                }}
                            >
                                <Menu.ItemIcon>
                                    <UploadFileRounded
                                        size={18}
                                        color="$gray11"
                                    />
                                </Menu.ItemIcon>
                                <Menu.ItemTitle>Upload Completed Form</Menu.ItemTitle>
                            </Menu.Item>
                        )}
                        {formStage != FormStage.none && (
                            <Menu.Item
                                key="abandon"
                                onPress={() => setShowAbandonFormDialog(formType)}
                            >
                                <Menu.ItemIcon>
                                    <DeleteRounded
                                        size={18}
                                        color="$red10"
                                    />
                                </Menu.ItemIcon>
                                <Menu.ItemTitle color="$red10">Abandon Form</Menu.ItemTitle>
                            </Menu.Item>
                        )}
                    </Menu.Content>
                </Menu.Portal>
            </Menu>
            <View flex={1} />
            {formStage != FormStage.none && (
                <View>
                    <ViewFormButton />
                </View>
            )}
            {formStage == FormStage.none && (
                <OptionalTooltip
                    show={typeof createDisabled == 'string'}
                    title={createDisabled as string}
                >
                    <XStack gap="$2">
                        <CreateEsignFormButton />
                    </XStack>
                </OptionalTooltip>
            )}
        </XStack>
    )

    // BodySection Component
    const BodySection = () => {
        const showSnackInner = useShowSnack()
        const retryMutation = useMutation(
            trpc.xero.retryEngagementInvoice.mutationOptions({
                onSuccess: () => {
                    showSnackInner('Engagement invoice created', 'success')
                    refreshPurchaser()
                },
                onError: err => {
                    showSnackInner(err.message || 'Failed to create invoice', 'danger')
                },
            }),
        )

        // Handle manual upload case
        if (form?.formStatus == 'manual upload') {
            return (
                <SizableText
                    fontSize="$4"
                    marginTop="$2"
                >
                    Form manually uploaded
                </SizableText>
            )
        }

        const allSteps: {
            key: string
            label: React.ReactNode
            completed: boolean
            color?: string
            icon?: React.ReactNode
        }[] = []

        // Step 1: Create
        allSteps.push({
            key: 'create',
            label: <Text>{manualFileUpload ? 'Upload' : 'Create eSign document'}</Text>,
            completed: formStage >= FormStage.created,
            icon:
                formStage != FormStage.none ? (
                    <Check
                        size={14}
                        color="white"
                    />
                ) : undefined,
        })

        // Step 2: Send
        allSteps.push({
            key: 'send',
            label: <Text>Send</Text>,
            completed: formStage >= FormStage.sent,
            icon:
                formStage >= FormStage.sent ? (
                    <Check
                        size={14}
                        color="white"
                    />
                ) : undefined,
        })

        // Recipient steps
        if (recipients.length > 0) {
            recipients.forEach(recipient => {
                const isSigned = recipient.status === 'completed' || recipient.status === 'signed'
                const isDeclined = recipient.status === 'declined'
                const isDelivered = recipient.status === 'delivered'
                allSteps.push({
                    key: recipient.id,
                    label: (
                        <YStack>
                            <Text>{recipient.name} Signature</Text>
                            {recipient.declined_reason && (
                                <SizableText fontSize="$1">reason: {recipient.declined_reason}</SizableText>
                            )}
                        </YStack>
                    ),
                    completed: isSigned || isDeclined,
                    color: isDeclined ? 'red' : undefined,
                    icon: isSigned ? (
                        <Check
                            size={14}
                            color="white"
                        />
                    ) : isDeclined ? (
                        <Cancel
                            size={14}
                            color="white"
                        />
                    ) : isDelivered ? (
                        <MoveToInbox
                            size={14}
                            color="white"
                        />
                    ) : undefined,
                })
            })
        } else {
            roleNames.forEach(roleName => {
                allSteps.push({
                    key: roleName,
                    label: <Text>{roleName} Signature</Text>,
                    completed: false,
                })
            })
        }

        // Add engagement invoice step for agentAppointmentForm
        if (formType === 'agentAppointmentForm') {
            const invoice = purchaser.engagementInvoice
            const formCompleted = form?.formStatus === 'completed'
            const subtext = getInvoiceStepSubtext(invoice, formCompleted)
            const showRetry = (!invoice && formCompleted) || !!invoice?.sendError

            allSteps.push({
                key: 'engagement-invoice',
                label: (
                    <YStack gap="$1">
                        <Text>Send Engagement Invoice</Text>
                        {subtext && (
                            <Text
                                fontSize="$2"
                                color="$gray11"
                            >
                                {subtext}
                                {invoice?.sendError && ` - ${invoice.sendError}`}
                            </Text>
                        )}
                        {showRetry && (
                            <XStack
                                gap="$2"
                                alignItems="center"
                            >
                                {retryMutation.isPending ? (
                                    <XStack
                                        gap="$1"
                                        alignItems="center"
                                    >
                                        <Spinner size="small" />
                                        <Text
                                            fontSize="$2"
                                            color="$gray11"
                                        >
                                            Creating...
                                        </Text>
                                    </XStack>
                                ) : (
                                    <Text
                                        fontSize="$2"
                                        cursor="pointer"
                                        color="$primary"
                                        textDecorationLine="underline"
                                        onPress={() =>
                                            retryMutation.mutate({ purchaserId: purchaser.purchaserId })
                                        }
                                    >
                                        {!invoice ? 'Create invoice' : 'Retry'}
                                    </Text>
                                )}
                                {invoice?.xeroInvoiceId && (
                                    <>
                                        <Text
                                            fontSize="$2"
                                            color="$gray11"
                                        >
                                            •
                                        </Text>
                                        <Text
                                            fontSize="$2"
                                            cursor="pointer"
                                            color="$primary"
                                            textDecorationLine="underline"
                                            onPress={() =>
                                                window.open(
                                                    `https://go.xero.com/AccountsReceivable/View.aspx?InvoiceID=${invoice.xeroInvoiceId}`,
                                                    '_blank',
                                                )
                                            }
                                        >
                                            View in Xero
                                        </Text>
                                    </>
                                )}
                            </XStack>
                        )}
                    </YStack>
                ),
                completed: invoice?.xeroStatus === 'AUTHORISED' || invoice?.xeroStatus === 'PAID',
                color: getInvoiceStepColor(invoice, formCompleted),
                icon: getInvoiceStepIcon(invoice, formCompleted),
            })
        }

        // Add invoice paid step for agentAppointmentForm
        if (formType === 'agentAppointmentForm') {
            const invoice = purchaser.engagementInvoice
            const xeroStatus = invoice?.xeroStatus
            const isPaid = xeroStatus === 'PAID'
            const subtext = getInvoicePaymentStepSubtext(xeroStatus)

            allSteps.push({
                key: 'invoice-paid',
                label: (
                    <YStack gap="$1">
                        <Text>Invoice Paid</Text>
                        {subtext && (
                            <Text
                                fontSize="$2"
                                color="$gray11"
                            >
                                {subtext}
                            </Text>
                        )}
                        {invoice?.xeroInvoiceId && (
                            <Text
                                fontSize="$2"
                                cursor="pointer"
                                color="$primary"
                                textDecorationLine="underline"
                                onPress={() =>
                                    window.open(
                                        `https://go.xero.com/AccountsReceivable/View.aspx?InvoiceID=${invoice.xeroInvoiceId}`,
                                        '_blank',
                                    )
                                }
                            >
                                View in Xero
                            </Text>
                        )}
                    </YStack>
                ),
                completed: isPaid,
                color: getInvoicePaymentStepColor(xeroStatus),
                icon: getInvoicePaymentStepIcon(xeroStatus),
            })
        }

        return (
            <>
                {formStage != FormStage.manual && (
                    <YStack
                        marginTop="$4"
                        width="100%"
                    >
                        {allSteps.map((step, i) => (
                            <StepItem
                                key={step.key}
                                indicator={
                                    <StepCircle
                                        completed={step.completed}
                                        color={step.color}
                                    >
                                        {step.icon}
                                    </StepCircle>
                                }
                                active={step.completed}
                                isLast={i === allSteps.length - 1}
                            >
                                {step.label}
                            </StepItem>
                        ))}
                    </YStack>
                )}
            </>
        )
    }

    return (
        <>
            <HeaderSection />
            <BodySection />
            {formType === 'agentAppointmentForm' && !purchaser.commitmentFee && (
                <XStack
                    backgroundColor="$statusWarnSoft"
                    padding="$3"
                    borderRadius="$4"
                    marginTop="$2"
                    gap="$2.5"
                    alignItems="flex-start"
                >
                    <ErrorOutlineRounded
                        size={16}
                        color="$statusWarn"
                    />
                    <Text
                        fontSize="$2"
                        color="$statusWarn"
                        flex={1}
                    >
                        No commitment fee set for this client. Update the commitment fee in the Pricing
                        section above.
                    </Text>
                </XStack>
            )}
            {previewPdf?.url && (
                <PdfModal
                    title={title}
                    pdf={previewPdf.url}
                    onClose={() => setPreviewPdf(undefined)}
                    fileName={`purchaser_${formType}.pdf`}
                    withAuth={previewPdf.useAuth}
                    isRawPdf={previewPdf.isRawPdf}
                />
            )}
            <IframeModal
                title={getFormName(formType)}
                url={editUrl}
                onClose={() => setEditUrl(undefined)}
                signingService={signingService}
                formType={formType}
                purchaserId={purchaser.purchaserId}
            />
            <UploadFormModal
                open={showUploadForm}
                onClose={url => {
                    setShowUploadForm(false)
                    if (url) {
                        console.log('url', url)
                        setEditUrl(url)
                    }
                }}
                formType={formType}
                signingService={signingService}
            />
            <AbandonFormModal
                open={showAbandonFormDialog != undefined}
                onClose={() => setShowAbandonFormDialog(undefined)}
                formType={showAbandonFormDialog || formType}
            />
            <AddAttachmentModal
                open={showAddAttachmentsModal}
                onClose={data => {
                    if (data) {
                        setEditUrl(data.editUrl)
                    }
                    setShowAddAttachmentsModal(false)
                }}
                formType={formType}
            />
            <ManualFormUploadModal
                open={showManualUploadModal != undefined}
                close={() => setShowManualUploadModal(undefined)}
                formType={showManualUploadModal!}
            />
            <PdfFormEditorModal
                open={showPdfEditor}
                purchaserId={purchaser.purchaserId}
                onClose={() => setShowPdfEditor(false)}
                onCreated={url => {
                    setShowPdfEditor(false)
                    setEditUrl(url)
                }}
            />
        </>
    )
}

export default EsignFormStepper
